// src/utils/doorloopjePdf.js
//
// De pdf van een doorloopje (A4 liggend), gemaakt van zijn verborgen pdf-container
// (.pdf-export-container): één afbeelding over de volle breedte van de pagina. Is de
// inhoud hoger dan een pagina, dan gaat hij verder op de volgende pagina. De kop en de
// beschrijving blijven heel. Een rij stappen loopt door: elke stap op zijn eigen regel,
// met zijn afbeelding bij zijn titel, en zijn aantekeningenlijnen bij elkaar en bij zijn
// laatste twee regels. Op één pagina staan de aantekeningenlijnen van een rij op één
// hoogte; loopt de pdf door, dan staan ze direct onder de tekst van hun eigen stap.

/** Liggend A4, in mm. */
export const PAGINA = { breedte: 297, hoogte: 210, marge: 5 };

/** Hoeveel px van de inhoud op een pagina past, als die `breedte` px breed is. */
function paginaPxBij(breedte, pagina = PAGINA) {
  return ((pagina.hoogte - 2 * pagina.marge) * breedte) / (pagina.breedte - 2 * pagina.marge);
}

/** Inhoud tot zoveel keer een pagina gaat verkleind op één pagina; hoger gaat door op een volgende. */
export const MAX_VERKLEIND = 1.15;

/** Wat naast elke regel tekst en de stukken van elke stap heel blijft op één pagina, als het erop past. */
const HEEL = '.pdf-header, .pdf-description, .pdf-footer, img';

/**
 * Waar een snede valt die zo laag mogelijk ligt, uiterlijk op `grens`: omhoog tot boven elk
 * stuk dat hij raakt. Een stuk dat hoger is dan een pagina, of op deze pagina al begon
 * (op of boven `van`), wordt wel gesneden.
 */
function snede(stukken, van, grens, paginaPx) {
  let y = grens;
  let geraakt = true;
  while (geraakt) {
    geraakt = false;
    for (const { boven, onder } of stukken) {
      if (boven > van && boven < y && onder > y && onder - boven <= paginaPx) {
        y = boven;
        geraakt = true;
      }
    }
  }
  return y;
}

/**
 * Hoe de inhoud over de pagina's gaat. Loopt een rij stappen die hoger is dan een pagina
 * door op de volgende, dan snijdt elke stap op zijn eigen regel. Een stap gaat daarna op
 * dezelfde hoogte verder als op de vorige pagina, zodat de aantekeningenlijnen van de rij
 * gelijk blijven staan. Wat al op de vorige pagina stond, of pas op de volgende komt, is wit.
 * @param {{ breedte: number, hoogte: number, heel: Rechthoek[], kolommen: Rechthoek[] }} inhoud
 *   maat van de inhoud in px; elk stuk dat heel blijft als het op een pagina past; elke stap
 * @returns {Array<{ van: number, tot: number, wit: Rechthoek[], x: number, y: number, breedte: number, hoogte: number }>}
 *   per pagina het deel van de inhoud (van/tot en wit in px) en waar het staat (x, y, breedte, hoogte in mm)
 * @typedef {{ boven: number, onder: number, links: number, rechts: number }} Rechthoek  in px vanaf linksboven
 */
export function paginaIndeling({ breedte, hoogte, heel, kolommen = [] }, pagina = PAGINA) {
  const mmPerPx = (pagina.breedte - 2 * pagina.marge) / breedte;
  const paginaPx = paginaPxBij(breedte, pagina);
  if (hoogte > 0 && hoogte <= paginaPx * MAX_VERKLEIND) {
    const schaal = Math.min(mmPerPx, (pagina.hoogte - 2 * pagina.marge) / hoogte);
    const b = breedte * schaal;
    return [{ van: 0, tot: hoogte, wit: [], x: (pagina.breedte - b) / 2, y: pagina.marge, breedte: b, hoogte: hoogte * schaal }];
  }
  const paginas = [];
  let van = 0;
  let vorige = []; // per kolom van de vorige snede: tot waar hij al op de vorige pagina stond
  for (;;) {
    const grens = van + paginaPx;
    const laatste = hoogte <= grens;
    let sneden = [];
    if (!laatste) {
      const doorlopend = kolommen.filter((k) => k.boven < grens && k.onder > grens);
      const banen = doorlopend.length ? doorlopend : [{ links: 0, rechts: breedte }];
      sneden = banen.map(({ links, rechts }) => ({
        links,
        rechts,
        y: snede(heel.filter((s) => s.links < rechts && s.rechts > links), van, grens, paginaPx),
      }));
    }
    const tot = laatste ? hoogte : Math.max(...sneden.map((s) => s.y));
    const wit = [
      ...vorige.map(({ links, rechts, y }) => ({ boven: van, onder: y, links, rechts })),
      ...sneden.map(({ links, rechts, y }) => ({ boven: y, onder: tot, links, rechts })),
    ].filter((w) => w.onder > w.boven);
    paginas.push({
      van,
      tot,
      wit,
      x: pagina.marge,
      y: pagina.marge,
      breedte: pagina.breedte - 2 * pagina.marge,
      hoogte: (tot - van) * mmPerPx,
    });
    if (laatste) return paginas;
    van = Math.min(...sneden.map((s) => s.y));
    vorige = sneden;
  }
}

/** De rechthoeken van `elementen` in px vanaf linksboven van `element`. */
function rechthoeken(element, elementen) {
  const { top, left } = element.getBoundingClientRect();
  return elementen
    .filter((r) => r.height > 0)
    .map((r) => ({ boven: r.top - top, onder: r.bottom - top, links: r.left - left, rechts: r.right - left }));
}

/**
 * Elke regel tekst en elk element van HEEL in `element`, en per stap: tot en met zijn titel,
 * en zijn aantekeningenlijnen met zijn laatste twee regels.
 */
function heleStukken(element) {
  const stukken = Array.from(element.querySelectorAll(HEEL), (el) => el.getBoundingClientRect());
  element.querySelectorAll('.pdf-step').forEach((stap) => {
    const { top, left, right } = stap.getBoundingClientRect();
    const titel = stap.querySelector('.pdf-step-header');
    if (titel) stukken.push(new DOMRect(left, top, right - left, titel.getBoundingClientRect().bottom - top));
    const lijnen = stap.querySelector('.pdf-notes-column');
    if (lijnen) {
      // De onderkant van de tekst zelf: het vak .pdf-step-content rekt mee tot de hoogte van de rij.
      const inhoud = document.createRange();
      inhoud.selectNodeContents(stap.querySelector('.pdf-step-content'));
      const tekst = inhoud.getBoundingClientRect().bottom;
      const regel = parseFloat(getComputedStyle(stap.querySelector('.pdf-step-description') || stap).lineHeight) || 0;
      const boven = Math.max(titel ? titel.getBoundingClientRect().bottom : top, tekst - 2 * regel);
      stukken.push(new DOMRect(left, boven, right - left, lijnen.getBoundingClientRect().bottom - boven));
    }
  });
  const tekst = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const bereik = document.createRange();
  for (let knoop = tekst.nextNode(); knoop; knoop = tekst.nextNode()) {
    bereik.selectNodeContents(knoop);
    stukken.push(...bereik.getClientRects());
  }
  return rechthoeken(element, stukken);
}

/** Of `blad` te lezen is: een stylesheet van een ander domein is dat niet. */
function leesbaar(blad) {
  try {
    return Boolean(blad.cssRules);
  } catch {
    return false;
  }
}

/**
 * Opties voor html2canvas waarmee de kopie van de pagina de stijlen krijgt die de pagina al
 * heeft geladen. De links naar die stylesheets gaan niet mee in de kopie, dus hij haalt geen
 * stylesheet op: in een tabblad van vóór een nieuwe versie bestaat dat bestand niet meer.
 * Een stylesheet van een ander domein is niet te lezen: die blijft een link.
 */
export function metLiveStijlen(opties = {}) {
  const overslaan = (el) =>
    el.tagName === 'LINK' && /(^|\s)stylesheet(\s|$)/i.test(el.rel) && !(el.sheet && !leesbaar(el.sheet));
  return {
    ...opties,
    ignoreElements: (el) => overslaan(el) || Boolean(opties.ignoreElements?.(el)),
    onclone: (doc, ...rest) => {
      const css = Array.from(document.styleSheets)
        .filter((blad) => blad.ownerNode?.tagName === 'LINK' && leesbaar(blad))
        .map((blad) => Array.from(blad.cssRules, (regel) => regel.cssText).join('\n'));
      const stijl = doc.createElement('style');
      stijl.textContent = css.join('\n');
      doc.head.appendChild(stijl);
      return opties.onclone?.(doc, ...rest);
    },
  };
}

/** Waar de inhoud van `element` echt eindigt, zonder de ruimte eronder. */
function inhoudHoogte(element) {
  const einde = Math.max(0, ...heleStukken(element).map((s) => s.onder));
  return Math.min(element.offsetHeight, Math.ceil(einde));
}

/** Maakt de pdf van `element` en slaat hem op als `bestandsnaam`. */
export async function bewaarDoorloopjePdf(element, bestandsnaam) {
  // Pas hier geladen: zo blijven ze buiten de bundel van de site.
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  // Loopt de inhoud door, dan staan de aantekeningenlijnen direct onder hun eigen stap:
  // onderaan de rij zouden ze bij een korte stap ver onder zijn tekst hangen.
  const hoogte = inhoudHoogte(element);
  const doorlopend = hoogte > paginaPxBij(element.offsetWidth) * MAX_VERKLEIND;
  element.classList.toggle('pdf-doorlopend', doorlopend);
  let canvas;
  let indeling;
  try {
    canvas = await html2canvas(element, metLiveStijlen({
      scale: 1.5,
      useCORS: true,
      allowTaint: false,
      imageTimeout: 15000,
      backgroundColor: '#ffffff',
      logging: false,
    }));
    indeling = paginaIndeling({
      breedte: element.offsetWidth,
      hoogte: doorlopend ? inhoudHoogte(element) : hoogte,
      heel: heleStukken(element),
      kolommen: rechthoeken(element, Array.from(element.querySelectorAll('.pdf-step'), (el) => el.getBoundingClientRect())),
    });
  } finally {
    element.classList.remove('pdf-doorlopend');
  }
  const schaal = canvas.width / element.offsetWidth;
  const pdf = new jsPDF('l', 'mm', 'a4');
  indeling.forEach((deel, i) => {
    if (i > 0) pdf.addPage();
    const sy = Math.round(deel.van * schaal);
    const sh = Math.min(canvas.height, Math.round(deel.tot * schaal)) - sy;
    const stuk = document.createElement('canvas');
    stuk.width = canvas.width;
    stuk.height = sh;
    const tekenen = stuk.getContext('2d');
    tekenen.drawImage(canvas, 0, sy, canvas.width, sh, 0, 0, canvas.width, sh);
    tekenen.fillStyle = '#ffffff';
    deel.wit.forEach((w) => {
      tekenen.fillRect(w.links * schaal, w.boven * schaal - sy, (w.rechts - w.links) * schaal, (w.onder - w.boven) * schaal);
    });
    pdf.addImage(stuk.toDataURL('image/jpeg', 0.85), 'JPEG', deel.x, deel.y, deel.breedte, deel.hoogte);
  });
  pdf.save(bestandsnaam);
}
